<template>
    <Dropdown v-model="isOpen" class="ew-smart-search-dropdown">
        <template #trigger>
            <button
                class="tw-w-full tw-bg-white tw-px-3.5 tw-py-1.5 tw-rounded-md tw-text-sm tw-leading-5 tw-bg-transparent tw-border tw-border-gray-300 tw-flex tw-items-center tw-justify-between tw-transition-colors tw-duration-150 focus:tw-outline-none focus:tw-ring-0 focus:tw-border-gray-600"
                :class="{
                    'tw-border-gray-600': isOpen,
                    'hover:tw-border-gray-400': !disabled && !loading,
                    'tw-opacity-50 tw-cursor-not-allowed': disabled,
                    'tw-cursor-wait': loading && !disabled,
                    '!tw-bg-white/10 !tw-text-white !tw-border-white/30 hover:!tw-bg-white/15 hover:!tw-border-white/50 focus:!tw-bg-white/15 focus:!tw-border-white/70':
                        glass,
                }"
                :disabled="disabled || loading"
            >
                <span class="tw-truncate tw-text-left">{{ displayText }}</span>
                <i v-if="loading" class="mdi mdi-loading tw-ml-2 tw-text-sm tw-opacity-50 tw-animate-spin"></i>
                <i
                    v-else
                    class="mdi mdi-chevron-down tw-ml-2 tw-text-sm tw-opacity-50 tw-transition-transform"
                    :class="{ 'tw-rotate-180': isOpen }"
                ></i>
            </button>
        </template>
        <div
            class="tw-mt-1 tw-bg-white tw-shadow-lg tw-rounded-md tw-border tw-border-gray-200"
            :class="{ 'tw-left-0': align === 'start', 'tw-right-0': align === 'end' }"
        >
            <div class="tw-p-2 tw-border-b tw-border-gray-100">
                <div class="tw-relative tw-flex tw-items-center tw-gap-2.5 tw-px-2">
                    <i class="mdi mdi-magnify tw-scale-125 tw-text-sm tw-text-gray-400"></i>
                    <input
                        ref="searchInput"
                        v-model="searchTerm"
                        :placeholder="`Search ${type}...`"
                        class="tw-w-full tw-pb-1 tw-pt-0 tw-text-sm tw-leading-5 focus:tw-outline-none"
                    />
                    <button v-if="searchTerm" class="tw-relative tw-top-[-1px]" @click="searchTerm = ''">
                        <i class="mdi mdi-close tw-text-gray-400 tw-text-sm"></i>
                    </button>
                    <Button
                        v-if="clearable && (Array.isArray(selected) ? selected.length > 0 : !!selected)"
                        class="tw-flex tw-relative tw-top-[-1px]"
                        compact
                        @click="clearSelection()"
                    >
                        Clear
                    </Button>
                </div>
            </div>

            <div class="tw-max-h-64 tw-overflow-y-auto tw-select-none">
                <div v-if="isLoadingRemote" class="tw-p-4 tw-text-sm tw-text-gray-500 tw-text-center">Searching...</div>
                <div v-else-if="filteredItems.length === 0" class="tw-p-4 tw-text-sm tw-text-gray-500 tw-text-center">
                    No {{ type }} found.
                </div>

                <div v-else class="tw-p-1 tw-flex tw-flex-col tw-gap-1">
                    <div
                        v-if="allowAllSelection && multiple"
                        class="tw-flex tw-items-center tw-px-2 tw-py-1.5 tw-text-xs hover:tw-bg-gray-100 tw-cursor-pointer tw-rounded-md"
                        @click="selectAllItems"
                    >
                        <i
                            class="mdi mdi-check tw-scale-125 tw-mr-2 tw-text-sm"
                            :class="
                                (Array.isArray(selected) ? selected.length : 0) === 0
                                    ? 'tw-opacity-100'
                                    : 'tw-opacity-0'
                            "
                        ></i>
                        All {{ type }}
                    </div>

                    <div
                        v-for="item in displayedItems"
                        :key="item.id"
                        class="tw-flex tw-items-center tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-gray-100 tw-cursor-pointer tw-rounded-md tw-transition-colors"
                        :class="{
                            'tw-bg-blue-50 tw-text-blue-600': isSelected(item),
                        }"
                        style="transition: all 0.2s ease"
                        @click="toggleItem(item)"
                    >
                        <i
                            class="mdi mdi-check tw-scale-125 tw-mr-2 tw-text-sm"
                            :class="isSelected(item) ? 'tw-opacity-100' : 'tw-opacity-0'"
                        ></i>
                        <span class="tw-truncate tw-flex-1">
                            <slot name="item" :item>{{ item.name }}</slot>
                        </span>
                        <i v-if="item.recent" class="mdi mdi-star tw-ml-auto tw-text-xs tw-text-yellow-500"></i>
                    </div>
                    <div v-if="filteredItems.length > 50" class="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500">
                        ... and {{ filteredItems.length - 50 }} more
                    </div>
                </div>
            </div>
        </div>
    </Dropdown>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, watch } from 'vue';
import { axios } from '@/js/utils/axios.ts';
import debounce from 'lodash/debounce';
import Dropdown from '@/js/components/controls/Dropdown.vue';
import Button from '@/js/components/controls/Button.vue';

type Item = { id: number | string; name: string; recent?: boolean };

const props = withDefaults(
    defineProps<{
        type?: string;
        placeholder?: string;
        items?: Array<Item>;
        endpoint?: string;
        align?: 'start' | 'end';
        allowAllSelection?: boolean;
        multiple?: boolean;
        clearable?: boolean;
        disabled?: boolean;
        loading?: boolean;
        glass?: boolean;
    }>(),
    {
        type: 'items',
        placeholder: 'No Items',
        items: () => [],
        align: 'start',
        allowAllSelection: false,
        multiple: false,
        clearable: false,
        disabled: false,
        loading: false,
        glass: false,
    }
);

// Emits
const emit = defineEmits(['update:selected']);

// Reactive state
const isOpen = ref(false);
const selected = defineModel<null | Array<number | string> | number | string>({ default: null });
const searchTerm = ref('');

onBeforeMount(() => {
    if (props.multiple && !Array.isArray(selected.value)) {
        selected.value = [];
    }
});

// Remote search (when endpoint is provided)
const remoteItems = ref<Item[]>([]);
const isLoadingRemote = ref(false);

const fetchRemoteItems = debounce(async () => {
    if (!props.endpoint) return;

    isLoadingRemote.value = true;
    try {
        const url = new URL(props.endpoint);
        if (searchTerm.value) {
            url.searchParams.append('query', searchTerm.value);
        }

        const selectedIds = Array.isArray(selected.value) ? selected.value : selected.value ? [selected.value] : [];
        selectedIds.forEach((id) => url.searchParams.append('selected[]', String(id)));

        const { data } = await axios.get(url.toString());
        remoteItems.value = Array.isArray(data) ? data : (data.data ?? []);
    } finally {
        isLoadingRemote.value = false;
    }
}, 300);

watch(searchTerm, () => {
    if (props.endpoint) {
        fetchRemoteItems();
    }
});

watch(isOpen, (open) => {
    if (open && props.endpoint) {
        fetchRemoteItems();
    }
});

// Computed properties
const filteredItems = computed(() => {
    if (props.endpoint) return remoteItems.value;
    if (!searchTerm.value) return props.items;
    return props.items.filter((item) => item.name.toLowerCase().includes(searchTerm.value.toLowerCase()));
});

const displayedItems = computed(() => {
    return filteredItems.value.slice(0, 50);
});

const allKnownItems = computed(() => {
    if (props.endpoint) return remoteItems.value;
    return props.items;
});

const displayText = computed(() => {
    if (!props.multiple) {
        if (selected.value) {
            return allKnownItems.value.find((i) => i.id === selected.value)?.name ?? props.placeholder;
        }
        return props.placeholder;
    }
    if (!selected.value) {
        return props.placeholder;
    } else if (Array.isArray(selected.value) && selected.value.length === 0) {
        return props.placeholder;
    } else if (Array.isArray(selected.value) && selected.value.length === 1) {
        const sel = selected.value;
        return allKnownItems.value.find((i) => i.id === sel[0])?.name ?? props.placeholder;
    } else if (Array.isArray(selected.value)) {
        return `${selected.value.length} selected`;
    }
    return props.placeholder;
});

// Methods
const closeDropdown = () => {
    isOpen.value = false;
    searchTerm.value = '';
};

const selectAllItems = () => {
    selected.value = [];
    emit('update:selected', selected.value);
    closeDropdown();
};

function clearSelection() {
    if (!props.clearable) return;
    if (!props.multiple) {
        selected.value = null;
        emit('update:selected', selected.value);
        return;
    }
    selected.value = [];
    emit('update:selected', selected.value);
}

const toggleItem = (item: { id: number | string; name: string; recent?: boolean }) => {
    if (props.multiple) {
        if (!Array.isArray(selected.value)) return;
        const index = selected.value.findIndex((s: number | string) => s === item.id);
        if (index > -1) {
            selected.value.splice(index, 1);
        } else {
            selected.value.push(item.id);
        }
        emit('update:selected', selected.value);
        return;
    }
    // Single selection mode
    selected.value = item.id;
    emit('update:selected', selected.value);
    closeDropdown();
};

const isSelected = (item: { id: number | string; name: string; recent?: boolean }) => {
    if (!props.multiple) {
        return selected.value === item.id;
    }
    if (!Array.isArray(selected.value)) return false;
    return selected.value.some((s: number | string) => s === item.id);
};
</script>
